/**
 * The shared create and edit sheet: a name field with a live 30-character
 * counter, an eight-swatch keyboard-selectable color grid, a live chip
 * preview, and a primary that stays disabled until both name and color are
 * set. In edit mode it states the propagation plainly and works identically
 * on an archived type and an active one.
 */
import {
  useAwardCustomCreate,
  useAwardCustomUpdate,
} from "@sportsmet/contest-sdk/private";
import {
  Button,
  cn,
  Dialog,
  Input,
  NEUTRAL_SURFACE_FRAME,
} from "@sportsmet/ui";
import { type ReactNode, useEffect, useState } from "react";

import { showApiError } from "../../errors/apiErrorBus";
import {
  AWARD_SWATCH_FRAME_CLASS,
  AWARD_SWATCH_SELECTED_CLASS,
  awardChipClass,
  awardColorRole,
  awardSwatchClass,
} from "../awards/awardChips";
import {
  AWARDS_EDIT_ARCHIVED_NOTE,
  AWARDS_EDIT_ARCHIVED_PILL,
  AWARDS_EDIT_SAVE_LABEL,
  AWARDS_EDIT_TITLE,
  AWARDS_FORM_CANCEL_LABEL,
  AWARDS_FORM_COLOR_LABEL,
  AWARDS_FORM_NAME_LABEL,
  AWARDS_FORM_PREVIEW_LABEL,
  AWARDS_FORM_PREVIEW_PLACEHOLDER,
  awardsCharCounter,
  awardsCreateSubmitLabel,
  awardsCreateTitle,
  awardsEditPropagationLine,
  type typeAwardKind,
} from "./poolAwardsCopy";

export type typeAwardTypeFormSheetEditing = {
  awardTypeId: string;
  name: string;
  color: string;
  status: "active" | "archived";
  grantCount: number;
};

export type typeAwardTypeFormSheetProps = {
  open: boolean;
  poolId: string;
  kind: typeAwardKind;
  /** null means create mode. */
  editing: typeAwardTypeFormSheetEditing | null;
  onOpenChange: (open: boolean) => void;
  onSaved: () => void;
};

export const AWARD_TYPE_FORM_SHEET_NAME = "award-type-form";

export const AWARD_COLORS = [
  "color1",
  "color2",
  "color3",
  "color4",
  "color5",
  "color6",
  "color7",
  "color8",
] as const;

export const AWARD_NAME_MAX_LENGTH = 30;
export const AWARD_NAME_WARNING_AT = 25;

type typeAwardColorValue = (typeof AWARD_COLORS)[number];

function toAwardColorValue(
  color: string | undefined,
): typeAwardColorValue | null {
  return (AWARD_COLORS as readonly string[]).includes(color ?? "")
    ? (color as typeAwardColorValue)
    : null;
}

export const AwardTypeFormSheet = ({
  open,
  poolId,
  kind,
  editing,
  onOpenChange,
  onSaved,
}: typeAwardTypeFormSheetProps): ReactNode => {
  const create = useAwardCustomCreate();
  const update = useAwardCustomUpdate();
  const isPending = create.isPending || update.isPending;

  const [name, setName] = useState(editing?.name ?? "");
  const [color, setColor] = useState<typeAwardColorValue | null>(
    toAwardColorValue(editing?.color),
  );

  // Re-seed from `editing` every time the sheet opens, so a stale value from
  // the previous type does not leak into the next one.
  useEffect(() => {
    if (!open) return;
    setName(editing?.name ?? "");
    setColor(toAwardColorValue(editing?.color));
  }, [open]);

  const handleSubmit = async (): Promise<void> => {
    if (name.trim() === "" || color === null) return;
    if (editing === null) {
      const result = await create.mutateAsync({
        poolId,
        kind,
        name: name.trim(),
        color,
      });
      if (showApiError(result)) return;
    } else {
      const result = await update.mutateAsync({
        poolId,
        awardTypeId: editing.awardTypeId,
        name: name.trim(),
        color,
      });
      if (showApiError(result)) return;
    }
    onSaved();
    onOpenChange(false);
  };

  const title = editing === null ? awardsCreateTitle(kind) : AWARDS_EDIT_TITLE;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      className="sm:max-w-[480px]"
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {AWARDS_FORM_CANCEL_LABEL}
          </Button>
          <Button
            variant="primary"
            data-testid="award-form-submit"
            disabled={name.trim() === "" || color === null || isPending}
            loading={isPending}
            onClick={() => void handleSubmit()}
          >
            {editing === null
              ? awardsCreateSubmitLabel(kind)
              : AWARDS_EDIT_SAVE_LABEL}
          </Button>
        </>
      }
    >
      {editing !== null && editing.status === "archived" ? (
        <div className="flex items-center gap-2">
          <span
            className={cn(
              "rounded-full bg-surface-elevated px-2 py-1 text-caption text-ink",
              NEUTRAL_SURFACE_FRAME,
            )}
            data-testid="award-form-archived-pill"
          >
            {AWARDS_EDIT_ARCHIVED_PILL}
          </span>
          <span className="text-caption text-ink-secondary">
            {AWARDS_EDIT_ARCHIVED_NOTE}
          </span>
        </div>
      ) : null}
      {editing !== null ? (
        <p className="text-body text-ink-secondary">
          {awardsEditPropagationLine(editing.grantCount)}
        </p>
      ) : null}

      <div className="flex flex-col gap-1">
        <Input
          label={AWARDS_FORM_NAME_LABEL}
          maxLength={AWARD_NAME_MAX_LENGTH}
          value={name}
          onChange={(event) => {
            setName(event.target.value);
          }}
        />
        <span
          className={cn(
            "self-end text-caption",
            name.length >= AWARD_NAME_WARNING_AT
              ? "font-semibold text-warning"
              : "text-ink-secondary",
          )}
          data-testid="award-name-counter"
        >
          {awardsCharCounter(name.length)}
        </span>
      </div>

      <div
        role="radiogroup"
        aria-label={AWARDS_FORM_COLOR_LABEL}
        className="grid grid-cols-4 gap-2"
      >
        {AWARD_COLORS.map((value) => {
          const isSelected = color === value;
          return (
            <button
              key={value}
              type="button"
              role="radio"
              aria-checked={isSelected}
              aria-label={value}
              data-testid={`award-color-${value}`}
              className={cn(
                awardSwatchClass(awardColorRole(value)),
                "flex min-h-12 min-w-11 items-center justify-center rounded-md",
                AWARD_SWATCH_FRAME_CLASS,
                isSelected ? AWARD_SWATCH_SELECTED_CLASS : null,
              )}
              onClick={() => {
                setColor(value);
              }}
            >
              {isSelected ? (
                <span
                  aria-hidden="true"
                  data-testid={`award-color-mark-${value}`}
                >
                  &#10003;
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      <div className="flex flex-col gap-1">
        <span className="text-caption text-ink-secondary">
          {AWARDS_FORM_PREVIEW_LABEL}
        </span>
        <div className="rounded-md border border-line p-3">
          {name.trim() !== "" && color !== null ? (
            <span
              data-testid="award-preview-chip"
              className={cn(
                "inline-flex rounded-sm px-3 py-2",
                awardChipClass(awardColorRole(color)),
                NEUTRAL_SURFACE_FRAME,
              )}
            >
              {name}
            </span>
          ) : (
            <span className="text-body text-ink-secondary">
              {AWARDS_FORM_PREVIEW_PLACEHOLDER}
            </span>
          )}
        </div>
      </div>
    </Dialog>
  );
};
